import { FormControl, FormHelperText, FormLabel } from '@chakra-ui/form-control';
import { Stack, Text, useMultiStyleConfig } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { forwardRef, ReactNode, Ref } from 'react';
import { FieldValues, Path } from 'react-hook-form';

import { useFieldValidationStates } from '../hooks/useFieldValidationStates';

export interface SimpleXProps<FormSchema extends FieldValues> {
  name: Path<FormSchema>;
  label?: string;
  helper?: string;
  errorMessage?: string;
  hideLowerErrorMessage?: boolean;
  removePadding?: boolean;
  children?: ReactNode;
}

type SimpleFieldProps<FormSchema, InputType> = SimpleXProps<FormSchema> & InputType;

export const SimpleField = forwardRef<HTMLDivElement, any>(
  (
    {
      name,
      label,
      children,
      helper,
      errorMessage,
      hideLowerErrorMessage = false,
      variant,
      isRequired,
      removePadding = false,
      isReactSelect = false,
      isDisabled = false,
      ...rest
    }: SimpleFieldProps<any, any>,
    ref?: Ref<HTMLDivElement>,
  ) => {
    const { error, isInvalid } = useFieldValidationStates(name as string);
    const validationMessage = errorMessage ?? error?.message;
    const styles = useMultiStyleConfig('SimpleField', {
      variant,
      isInvalid: isInvalid || !!errorMessage,
      removePadding,
    });

    return (
      // This should really be changed so that the component passes in a stackProps rather than just using ...rest
      <Stack spacing="1" {...rest} title={validationMessage ?? rest.title}>
        <FormControl
          name={name}
          id={name as string}
          isInvalid={isInvalid}
          isRequired={isRequired}
          isDisabled={isDisabled}
          ref={ref}
          {...styles.form}
          {...rest}
          title={validationMessage ?? rest.title}
        >
          <Stack justify="space-between" spacing={1} direction={rest.direction}>
            {label && (
              <FormLabel
                margin={0}
                variant="inputLabel"
                userSelect="none"
                p={isReactSelect ? 4 : 0}
                noOfLines={1}
                isTruncated
                pb="1"
                title={validationMessage ?? label}
              >
                {label}
              </FormLabel>
            )}
            {children}
          </Stack>

          {helper && <FormHelperText>{helper}</FormHelperText>}
        </FormControl>
        <Show if={!hideLowerErrorMessage && !!validationMessage}>
          <Text color="error" fontSize="xs" fontWeight="bold">
            {validationMessage}
          </Text>
        </Show>
      </Stack>
    );
  },
);
